/* Patch 15.4 — Bulk Critical Correction Wizard
   ================================================
   4-step wizard for bulk-correcting a CATEGORY of findings using a
   single strategy. Server enforces:
     • category & strategy_id are whitelisted
     • expected_count matches a fresh re-scan
     • confirmation_text equals "APPLY <count>"

   After success, the Post-Correction Verification card shows
   Repair-Readiness (PASS/FAIL per check + posting-eligible count). */
import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, Wrench, CheckCircle2, XCircle, ArrowRight, ArrowLeft,
  ListChecks, ShieldCheck,
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function BulkCorrectionWizard({
  category, categoryLabel, strategyId, findings, onClose, onApplied,
}) {
  const [step, setStep]               = useState(1);
  const [confirmation, setConfirmation] = useState('');
  const [applying, setApplying]       = useState(false);
  const [result, setResult]           = useState(null);
  const [readiness, setReadiness]     = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  const expectedCount = findings.length;
  const expectedConfirm = `APPLY ${expectedCount}`;

  const totalImpact = findings.reduce(
    (s, f) => s + (Number(f.variance) || 0), 0);

  const execute = async () => {
    setApplying(true);
    try {
      const r = await axiosInstance.post('/data-quality/bulk-apply', {
        category, strategy_id: strategyId,
        expected_count: expectedCount,
        confirmation_text: confirmation.toUpperCase().trim(),
      });
      setResult(r.data);
      setStep(4);
      // Auto-fetch readiness
      setReadinessLoading(true);
      try {
        const rd = await axiosInstance.get(
          '/data-quality/repair-readiness?doc_type=invoice');
        setReadiness(rd.data);
      } catch (e) {
        toast.error(`Readiness check failed: ${e.message}`);
      } finally {
        setReadinessLoading(false);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Bulk apply failed: ${
        typeof d === 'object'
          ? (d.error || JSON.stringify(d))
          : (d || e.message)
      }`);
    } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="bulk-correction-wizard">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500">Bulk Correction Wizard</div>
            <h2 className="text-base font-bold text-slate-900">
              {categoryLabel} — {expectedCount} document(s)
            </h2>
          </div>
          <StepIndicator step={step} />
        </header>

        <div className="p-5">
          {step === 1 && (
            <Step1 category={category} categoryLabel={categoryLabel}
                   strategyId={strategyId}
                   expectedCount={expectedCount}
                   onNext={() => setStep(2)} onCancel={onClose} />
          )}
          {step === 2 && (
            <Step2 findings={findings} totalImpact={totalImpact}
                   onBack={() => setStep(1)} onNext={() => setStep(3)} />
          )}
          {step === 3 && (
            <Step3 expectedConfirm={expectedConfirm}
                   confirmation={confirmation}
                   setConfirmation={setConfirmation}
                   applying={applying}
                   onBack={() => setStep(2)}
                   onApply={execute} />
          )}
          {step === 4 && (
            <Step4 result={result} readiness={readiness}
                   readinessLoading={readinessLoading}
                   onClose={async () => { await onApplied(); onClose(); }} />
          )}
        </div>
      </div>
    </div>
  );
}


function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-1" data-testid="wizard-step-indicator">
      {[1,2,3,4].map(n => (
        <div key={n} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
          n < step ? 'bg-emerald-500 text-white' :
          n === step ? 'bg-slate-900 text-white' :
          'bg-slate-200 text-slate-500'
        }`}>{n < step ? <CheckCircle2 size={12} /> : n}</div>
      ))}
    </div>
  );
}


function Step1({ category, categoryLabel, strategyId, expectedCount, onNext, onCancel }) {
  return (
    <div data-testid="wizard-step-1">
      <h3 className="text-sm font-bold text-slate-900 mb-3">Step 1 of 4 — Confirm Scope</h3>
      <div className="space-y-3">
        <Field label="Category">{categoryLabel} <code className="text-xs ml-1 text-slate-500">({category})</code></Field>
        <Field label="Strategy">
          <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{strategyId}</code>
        </Field>
        <Field label="Affected Documents">
          <span className="text-xl font-bold font-mono text-rose-700">{expectedCount}</span>
        </Field>
      </div>
      <Footer onCancel={onCancel} primary={{ label: 'Continue →', onClick: onNext, testid: 'wizard-step1-next' }} />
    </div>
  );
}


function Step2({ findings, totalImpact, onBack, onNext }) {
  return (
    <div data-testid="wizard-step-2">
      <h3 className="text-sm font-bold text-slate-900 mb-3">Step 2 of 4 — Review Impact Summary</h3>
      <div className="space-y-3">
        <Field label="Total Variance Impact">
          <span className={`text-xl font-bold font-mono ${
            totalImpact >= 0 ? 'text-emerald-700' : 'text-rose-700'
          }`}>
            {totalImpact >= 0 ? '+' : ''}{fmt(totalImpact)} SAR
          </span>
        </Field>
        <div className="border border-slate-200 rounded">
          <div className="text-xs font-bold text-slate-700 px-3 py-1.5 bg-slate-50">
            Documents (showing first 20 of {findings.length})
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {findings.slice(0, 20).map((f, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`wizard-doc-${i}`}>
                    <td className="px-3 py-1 font-mono">{f.doc_label}</td>
                    <td className="px-3 py-1 text-right font-mono text-slate-600">
                      {f.variance !== undefined ? `${f.variance > 0 ? '+' : ''}${fmt(f.variance)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer
        onCancel={onBack}
        cancelLabel="← Back"
        primary={{ label: 'Continue →', onClick: onNext, testid: 'wizard-step2-next' }}
      />
    </div>
  );
}


function Step3({ expectedConfirm, confirmation, setConfirmation, applying, onBack, onApply }) {
  const matches = confirmation.toUpperCase().trim() === expectedConfirm;
  return (
    <div data-testid="wizard-step-3">
      <h3 className="text-sm font-bold text-slate-900 mb-3">Step 3 of 4 — Double Confirmation</h3>
      <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-900 mb-3">
        <strong>Server-side guards:</strong>
        <ul className="list-disc list-inside mt-1 space-y-0.5">
          <li>Re-scan must produce exact match for the count above</li>
          <li>Confirmation text MUST equal <code className="bg-white px-1 rounded">{expectedConfirm}</code></li>
          <li>Strategy is whitelisted server-side — preview cannot bypass</li>
          <li>Each correction writes its own audit_log entry</li>
        </ul>
      </div>
      <label className="text-xs font-bold text-slate-700">
        Type <code className="bg-slate-100 px-1.5 py-0.5 rounded">{expectedConfirm}</code> to confirm:
      </label>
      <input
        data-testid="wizard-confirm-input"
        value={confirmation}
        onChange={e => setConfirmation(e.target.value)}
        placeholder={expectedConfirm}
        className="w-full mt-1 border border-slate-300 rounded px-3 py-2 font-mono text-sm uppercase"
        autoFocus
      />
      <Footer
        onCancel={onBack}
        cancelLabel="← Back"
        primary={{
          label: applying ? 'Applying…' : 'Apply All',
          onClick: onApply,
          testid: 'wizard-apply-btn',
          disabled: !matches || applying,
          icon: applying
            ? <Loader2 size={12} className="animate-spin" />
            : <Wrench size={12} />,
        }}
      />
    </div>
  );
}


function Step4({ result, readiness, readinessLoading, onClose }) {
  if (!result) return null;
  return (
    <div data-testid="wizard-step-4">
      <h3 className="text-sm font-bold text-slate-900 mb-3">Step 4 of 4 — Result & Post-Correction Verification</h3>

      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="wizard-result-summary">
        <Stat label="Attempted"  value={result.attempted}  cls="text-slate-900" />
        <Stat label="Succeeded"  value={result.succeeded}  cls="text-emerald-700" />
        <Stat label="Failed"     value={result.failed}     cls={result.failed > 0 ? "text-rose-700" : "text-slate-400"} />
      </div>

      <div className="border border-slate-200 rounded mb-4" data-testid="readiness-card">
        <div className="text-xs font-bold text-slate-700 px-3 py-2 bg-slate-50 inline-flex items-center gap-1 w-full">
          <ShieldCheck size={12} /> Post-Correction Verification
        </div>
        {readinessLoading && (
          <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Re-scanning…
          </div>
        )}
        {readiness && (
          <>
            <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between">
              <div className={`font-bold ${readiness.verdict === 'READY' ? 'text-emerald-700' : 'text-amber-700'}`}>
                Verdict: {readiness.verdict}
              </div>
              <div className="text-xs text-slate-500">
                Health Score: <span className="font-bold">{readiness.health_score}/100</span>
              </div>
            </div>
            <table className="w-full text-xs">
              <tbody>
                {readiness.checks.map((c, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`readiness-check-${i}`}>
                    <td className="px-3 py-1.5">
                      {c.status === 'PASS'
                        ? <CheckCircle2 size={11} className="text-emerald-600 inline mr-1" />
                        : <XCircle      size={11} className="text-rose-600   inline mr-1" />}
                      {c.name}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-bold text-xs ${c.status === 'PASS' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {c.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {readiness.eligible_by_type && (
              <div className="border-t border-slate-200 bg-emerald-50 px-3 py-3" data-testid="readiness-eligible">
                <ListChecks size={14} className="text-emerald-700 inline mr-1" />
                <strong className="text-emerald-900 text-sm">Ready for Bulk Repair:</strong>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {Object.entries(readiness.eligible_by_type).map(([dt, info]) => (
                    <div key={dt} className="text-xs text-emerald-900">
                      • {dt}: <strong>{info.eligible}</strong>/{info.total} eligible
                      {info.blocked > 0 && (
                        <span className="text-rose-700 ml-1">({info.blocked} still blocked)</span>
                      )}
                    </div>
                  ))}
                </div>
                {readiness.verdict === 'READY' && (
                  <a
                    href="/financial-repair-center"
                    data-testid="open-repair-center-link"
                    className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded px-3 py-1.5 mt-2"
                  >
                    Open Financial Repair Center <ArrowRight size={11} />
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end">
        <button
          data-testid="wizard-close-btn"
          onClick={onClose}
          className="px-3 py-1.5 text-xs rounded bg-slate-900 hover:bg-slate-800 text-white font-semibold"
        >
          Close
        </button>
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm text-slate-900">{children}</span>
    </div>
  );
}


function Stat({ label, value, cls }) {
  return (
    <div className="bg-slate-50 rounded p-2 border border-slate-200">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}


function Footer({ onCancel, cancelLabel = 'Cancel', primary }) {
  return (
    <div className="flex justify-end gap-2 mt-5">
      <button
        onClick={onCancel}
        className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
      >
        {cancelLabel.startsWith('←') && <ArrowLeft size={11} />}
        {cancelLabel.startsWith('←') ? cancelLabel.replace('←','').trim() : cancelLabel}
      </button>
      <button
        data-testid={primary.testid}
        onClick={primary.onClick}
        disabled={primary.disabled}
        className="px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
      >
        {primary.icon}
        {primary.label}
      </button>
    </div>
  );
}
